import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Wand2, Settings, Sun } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DayCard } from '@/components/dayPlanner/DayCard';
import { PlannerSettings } from '@/components/dayPlanner/PlannerSettings';

const err = (e, m) => toast.error(e?.response?.data?.detail || m);

function Pool({ pool, dates, onAdd }) {
  return (
    <div data-testid="planner-pool" className="border border-[#E2E2DF] rounded-md bg-white p-4">
      <p className="text-sm font-semibold text-slate-900 mb-2">Not planned yet ({pool.length})</p>
      {pool.length === 0 && <p className="text-xs text-slate-400">Everything due is planned.</p>}
      <ul className="space-y-2 max-h-[70vh] overflow-y-auto">{pool.map(v => (
        <li key={v.visit_id} data-testid={`pool-visit-${v.plan_id}`} className="text-xs border-t border-[#F0EFEC] pt-2 first:border-t-0">
          <p className="font-semibold text-slate-800">{v.client_name} <span className="font-normal text-slate-500">· {v.suburb}</span></p>
          <p className="text-slate-600">{v.labels.join(', ')} · {v.hours}h {v.calm_dry && <Sun size={10} className="inline text-amber-500" />} · due {v.earliest_due}{v.booking_flex === 'fixed' ? ` · fixed ${v.preferred_day}` : ''}</p>
          <select data-testid={`pool-add-${v.plan_id}`} value="" onChange={e => e.target.value && onAdd(v, e.target.value)} className="mt-1 border border-slate-200 rounded px-1 py-0.5">
            <option value="">Add to day…</option>{dates.map(d => <option key={d} value={d}>{d}</option>)}</select>
        </li>))}</ul>
    </div>
  );
}

export default function DayPlannerPage() {
  const { api } = useAuth();
  const [start, setStart] = useState('');
  const [days, setDays] = useState(10);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [emailClients, setEmailClients] = useState(false);
  const load = useCallback(() => api.get('/day-planner', { params: { start, days } }).then(r => { setData(r.data); if (!start) setStart(r.data.start); }).catch(e => err(e, 'Could not load planner')), [api, start, days]);
  useEffect(() => { load(); }, [load]);
  const build = async () => {
    setBusy(true);
    try { const r = await api.post('/day-planner/auto-build', { start, days }); toast.success(`Placed ${r.data.placed} visits over ${r.data.days_used} days${r.data.unplaced ? ` · ${r.data.unplaced} not placed` : ''}`); load(); }
    catch (e) { err(e, 'Build failed'); } finally { setBusy(false); }
  };
  const move = async (from, v, kind, arg) => {
    try {
      if (kind === 'reorder') await api.post('/day-planner/move', { visit_id: v.visit_id, from_date: from, to_date: from, to_index: arg });
      else if (kind === 'day') await api.post('/day-planner/move', { visit_id: v.visit_id, from_date: from, to_date: arg });
      else await api.post('/day-planner/move', { visit_id: v.visit_id, from_date: from, to_date: null });
      load();
    } catch (e) { err(e, 'Move failed'); }
  };
  const add = async (v, d) => { try { await api.post('/day-planner/move', { visit: v, to_date: d }); load(); } catch (e) { err(e, 'Could not add'); } };
  const confirm = async () => {
    const day = confirming; setConfirming(null);
    try {
      const r = await api.post(`/day-planner/${day.date}/confirm`, { email_clients: emailClients });
      const bad = r.data.results.filter(x => x.error);
      toast.success(`${r.data.results.length - bad.length} jobs booked${r.data.calendar ? ' and on the calendar' : ' (pick a crew in settings to add them to the calendar)'}`);
      bad.forEach(x => toast.error(`${x.client_name}: ${x.error}${r.data.returned_to_pool ? ' — moved back to Not planned' : ''}`));
      load();
    } catch (e) { err(e, 'Confirm failed'); }
  };
  const clear = async (day) => { try { await api.post(`/day-planner/${day.date}/clear`); load(); } catch (e) { err(e, 'Clear failed'); } };
  const dates = data?.days.map(d => d.date) || [];
  return (
    <div data-testid="day-planner-page" className="p-6 lg:p-8">
      <h1 className="text-2xl font-bold text-slate-900">Day planner</h1>
      <p className="text-sm text-slate-500 mt-1 mb-5">Due maintenance visits grouped by area into day lists. Treatments (<Sun size={12} className="inline text-amber-500" />) only go on dry, calm days or dry breaks long enough to apply and dry.</p>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <input data-testid="planner-start" type="date" value={start} onChange={e => setStart(e.target.value)} className="px-2 py-1.5 text-sm border border-slate-300 rounded" />
        <select data-testid="planner-days" value={days} onChange={e => setDays(Number(e.target.value))} className="px-2 py-1.5 text-sm border border-slate-300 rounded">{[5, 10, 15, 20].map(n => <option key={n} value={n}>{n} work days</option>)}</select>
        <button data-testid="planner-build-btn" onClick={build} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}Build day lists</button>
        <span className="text-xs text-slate-500">Rebuilding replaces draft days only — confirmed days stay.</span>
        <button data-testid="planner-settings-btn" onClick={() => setShowSettings(true)} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50"><Settings size={14} />Settings</button>
      </div>
      {!data ? <Loader2 className="animate-spin text-slate-400" /> : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-5 items-start">
          <div className="grid md:grid-cols-2 gap-4">{data.days.map(d => <DayCard key={d.date} day={d} allDates={dates} onMove={move} onConfirm={setConfirming} onClear={clear} />)}</div>
          <Pool pool={data.pool} dates={dates} onAdd={add} />
        </div>
      )}
      <PlannerSettings open={showSettings} onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); load(); }} />
      <Dialog open={!!confirming} onOpenChange={v => !v && setConfirming(null)}>
        <DialogContent data-testid="confirm-day-dialog">
          <DialogHeader><DialogTitle>Confirm {confirming?.date}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Creates {confirming?.visits.length} Work Booked maintenance job(s) and puts them on the calendar.</p>
          <label className="flex items-center gap-2 text-sm"><input data-testid="confirm-email-clients" type="checkbox" checked={emailClients} onChange={e => setEmailClients(e.target.checked)} />Email each client their booked day (real emails)</label>
          <button data-testid="confirm-day-submit" onClick={confirm} className="px-4 py-2 rounded bg-[#D35400] text-white hover:bg-[#A04000]">Confirm day</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
